import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../services/api';
import appConfig from '../config/appConfig';
import '../styles/PublicProfile.css';

const PublicProfile = () => {
  const { assetId } = useParams();
  const [profile, setProfile] = useState(null);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadPublicProfile = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get(`/api/assets/public/${assetId}`);
      setStatus(res.data.status);
      setProfile(res.data.profile || null);
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to load profile');
    } finally {
      setLoading(false);
    }
  }, [assetId]);

  useEffect(() => {
    loadPublicProfile();
  }, [loadPublicProfile]);

  if (loading) {
    return (
      <div className="public-profile-container">
        <div className="loading">Loading profile...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="public-profile-container">
        <div className="error-message">
          <h2>❌ {error}</h2>
          <p>This QR code is not recognized.</p>
        </div>
      </div>
    );
  }

  if (status === 'unregistered') {
    return (
      <div className="public-profile-container">
        <div className="status-message">
          <div className="profile-icon">🔗</div>
          <h2>Not Yet Registered</h2>
          <p>This QR code is not presently assigned to an account.</p>
          <p>If this is your {appConfig.appName} tag, log in or create an account to register it.</p>
          <div className="cta-buttons">
            <Link to="/login/user" className="cta-button">Log In</Link>
            <Link to="/register/user" className="cta-button cta-button-primary">Register</Link>
          </div>
        </div>
      </div>
    );
  }

  if (status === 'private') {
    return (
      <div className="public-profile-container">
        <div className="status-message">
          <div className="profile-icon">🔒</div>
          <h2>Profile Not Public</h2>
          <p>This QR code has been registered. No other identifiable information is available.</p>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="public-profile-container">
        <div className="error-message">
          <h2>Profile Not Found</h2>
        </div>
      </div>
    );
  }

  return (
    <div className="public-profile-container">
      <div className="public-profile-card">
        <div className="profile-header">
          <div className="profile-icon">👤</div>
          <h1>{profile.name}</h1>
          <p className="asset-info">
            Asset: <strong>{profile.assetId}</strong> ({profile.assetType})
          </p>
        </div>

        {profile.message && (
          <div className="custom-message">
            <p>{profile.message}</p>
          </div>
        )}

        {profile.medicalInfo && (
          <div className="medical-info-public">
            <h2>🏥 Medical Information</h2>

            {profile.medicalInfo.bloodType && (
              <div className="info-item">
                <span className="label">Blood Type:</span>
                <span className="value blood-type">{profile.medicalInfo.bloodType}</span>
              </div>
            )}

            {profile.medicalInfo.allergies && profile.medicalInfo.allergies.length > 0 && (
              <div className="info-section alert">
                <h3>⚠️ Allergies</h3>
                <ul className="medical-list">
                  {profile.medicalInfo.allergies.map((allergy, idx) => (
                    <li key={idx} className="alert-item">{allergy}</li>
                  ))}
                </ul>
              </div>
            )}

            {profile.medicalInfo.medications && profile.medicalInfo.medications.length > 0 && (
              <div className="info-section">
                <h3>💊 Current Medications</h3>
                <ul className="medical-list">
                  {profile.medicalInfo.medications.map((med, idx) => (
                    <li key={idx}>{med}</li>
                  ))}
                </ul>
              </div>
            )}

            {profile.medicalInfo.conditions && profile.medicalInfo.conditions.length > 0 && (
              <div className="info-section">
                <h3>📋 Medical Conditions</h3>
                <ul className="medical-list">
                  {profile.medicalInfo.conditions.map((condition, idx) => (
                    <li key={idx}>{condition}</li>
                  ))}
                </ul>
              </div>
            )}

            {profile.medicalInfo.emergencyContact && (
              <div className="info-section emergency">
                <h3>🚨 Emergency Contact</h3>
                <div className="contact-info">
                  <p><strong>Name:</strong> {profile.medicalInfo.emergencyContact.name}</p>
                  <p><strong>Phone:</strong> 
                    <a href={`tel:${profile.medicalInfo.emergencyContact.phone}`}>
                      {profile.medicalInfo.emergencyContact.phone}
                    </a>
                  </p>
                  <p><strong>Relationship:</strong> {profile.medicalInfo.emergencyContact.relationship}</p>
                </div>
              </div>
            )}
          </div>
        )}

        {(!profile.medicalInfo || Object.keys(profile.medicalInfo).length === 0) && (
          <div className="no-medical-info">
            <p>No public medical information available</p>
          </div>
        )}

        <div className="profile-footer">
          <p className="disclaimer">
            ℹ️ This is a public profile. Only information explicitly shared by the owner is displayed.
          </p>
          <p className="registration-date">
            Registered: {new Date(profile.registrationDate).toLocaleDateString()}
          </p>
        </div>
      </div>
    </div>
  );
};

export default PublicProfile;
